import {ApolloQueryResult} from 'apollo-client';
import gql from 'graphql-tag';
import {graphql} from 'react-apollo';
import {Settings, SettingsInput} from '../generated/graphql';

import {Graph, UserInfo, View} from '../types/graphql';

export interface UserContextResult {
  viewer: {
    views: Graph<Pick<View, 'id' | 'spec'>>;
  };
}

export const USER_CONTEXT_QUERY = gql`
  query UserContext {
    viewer {
      id
      views(viewType: "userContext") {
        edges {
          node {
            id
            spec
          }
        }
      }
    }
  }
`;

export const USER_ORGANIZATIONS_QUUERY = gql`
  query UserOrganizations {
    viewer {
      id
      organizations {
        id
        name
        orgType
      }
    }
  }
`;

export const ENTITY_QUERY = gql`
  query Entity($name: String!) {
    entity(name: $name) {
      id
      name
      available
      photoUrl
      readOnly
      readOnlyAdmin
      isTeam
      privateOnly
      storageBytes
      codeSavingEnabled
      settings {
        defaultCloudRegion {
          id
          provider
          region
        }
      }
      defaultAccess
      isPaid
      rateLimits {
        filestream
        graphql
      }
      members {
        id
        admin
        pending
        username
        name
        email
        photoUrl
        accountType
        apiKey
      }
      defaultAlerts {
        id
        condition {
          __typename
          ... on FinishedRunCondition {
            success
          }
          ... on StoppedRunCondition {
            minimumRunDuration
          }
        }
        subscriptions {
          __typename
          ... on EmailSubscription {
            id
          }
          ... on SlackChannelSubscription {
            id
          }
        }
      }
      integrations {
        edges {
          node {
            id
            __typename
            ... on SlackIntegration {
              teamName
              channelName
            }
          }
        }
      }
    }
    user(userName: $name) {
      id
      username
      accountType
    }
  }
`;

export const SEARCH_USERS_QUERY = gql`
  query SearchUsers($query: String) {
    users(query: $query) {
      edges {
        node {
          id
          name
          username
          email
          admin
          deletedAt
        }
      }
    }
  }
`;

export const SET_USER_ADMIN = gql`
  mutation SetUserAdmin($id: ID!, $admin: Boolean!) {
    updateUser(input: {id: $id, admin: $admin}) {
      user {
        id
        name
        username
        email
        admin
        deletedAt
      }
    }
  }
`;
interface SetUserAdminMutationVariables {
  id: string;
  admin: boolean;
}
interface SetUserAdminMutationData {
  user: {
    id: string;
    name: string;
    username: string;
    email: string;
    admin: boolean;
    deletedAt?: string;
  };
}

export const setUserAdminMutation = graphql<
  {},
  SetUserAdminMutationData,
  SetUserAdminMutationVariables,
  {}
>(SET_USER_ADMIN, {
  props: ({mutate}) => ({
    setUserAdmin: (variables: SetUserAdminMutationVariables) =>
      mutate!({variables}),
  }),
});

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(input: {id: $id}) {
      user {
        id
        name
        username
        email
        admin
        deletedAt
      }
    }
  }
`;
interface DeleteUserMutationVariables {
  id: string;
}
interface DeleteUserMutationData {
  user: {
    id: string;
    name: string;
    username: string;
    email: string;
    admin: boolean;
    deletedAt?: string;
  };
}

export const deleteUserMutation = graphql<
  {},
  DeleteUserMutationData,
  DeleteUserMutationVariables,
  {}
>(DELETE_USER, {
  props: ({mutate}) => ({
    deleteUser: (variables: DeleteUserMutationVariables) =>
      mutate!({variables}),
  }),
});

export const UNDELETE_USER = gql`
  mutation UndeleteUser($id: ID!) {
    undeleteUser(input: {id: $id}) {
      user {
        id
        name
        username
        email
        admin
        deletedAt
      }
    }
  }
`;

export const CREATE_INVITE = gql`
  mutation CreateInvite(
    $entityName: String!
    $email: String
    $username: String
    $admin: Boolean
  ) {
    createInvite(
      input: {
        entityName: $entityName
        email: $email
        username: $username
        admin: $admin
      }
    ) {
      invite {
        id
        name
        email
        createdAt
        toUser {
          name
        }
      }
    }
  }
`;

interface CreateInviteMutationData {
  createInvite: {
    invite: {
      id: string;
      name: string;
      email: string;
      createdAt: string;
      toUser: {
        name: string;
      };
    };
  };
}

type CreateInviteMutationVariables = {
  entityName: string;
  admin: boolean;
} & ({email: string} | {username: string});

export const createInviteMutation = graphql<
  {},
  CreateInviteMutationData,
  CreateInviteMutationVariables,
  {}
>(CREATE_INVITE, {
  props: ({mutate}) => ({
    createInvite: (variables: CreateInviteMutationVariables) =>
      mutate!({variables}),
  }),
});

export type CreateInviteMutationFn = (
  variables: CreateInviteMutationVariables
) => Promise<ApolloQueryResult<CreateInviteMutationData>>;

export const DELETE_INVITE = gql`
  mutation DeleteInvite($id: String, $entityName: String) {
    deleteInvite(input: {id: $id, entityName: $entityName}) {
      success
    }
  }
`;

export interface DeleteInviteMutationData {
  deleteInvite: {
    success: boolean;
  };
}

export interface DeleteInviteMutationVariables {
  id: string;
  entityName: string;
}

export const UPDATE_MEMBER = gql`
  mutation UpdateMember($entityName: String!, $user: ID!, $admin: Boolean!) {
    updateMember(input: {entityName: $entityName, user: $user, admin: $admin}) {
      member {
        id
        admin
      }
    }
  }
`;

export interface UpdateMemberMutationData {
  updateMember: {
    member: {
      id: string;
      admin: boolean;
    };
  };
}

export interface UpdateMemberMutationVariables {
  entityName: string;
  user: string;
  admin: boolean;
}

export const CREATE_SERVICE_ACCOUNT = gql`
  mutation CreateServiceAccount($entityName: String!, $description: String!) {
    createServiceAccount(
      input: {description: $description, entityName: $entityName}
    ) {
      user {
        id
        name
        apiKeys {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`;

interface CreateServiceAccountMutationData {
  createServiceAccount: {
    user: {
      id: string;
      name: string;
      apiKeys: {
        edges: Array<{
          node: {
            id: string;
            name: string;
          };
        }>;
      };
    };
  };
}

interface CreateServiceAccountMutationVariables {
  entityName: string;
  description: string;
}

export const createServiceAccountMutation = graphql<
  {},
  CreateServiceAccountMutationData,
  CreateServiceAccountMutationVariables,
  {}
>(CREATE_SERVICE_ACCOUNT, {
  props: ({mutate}) => ({
    createServiceAccount: (variables: CreateServiceAccountMutationVariables) =>
      mutate!({variables}),
  }),
});

export type CreateServiceAccountMutationFn = (
  variables: CreateServiceAccountMutationVariables
) => Promise<ApolloQueryResult<CreateServiceAccountMutationData>>;

// entity field must contain all fields that other viewer > teams queries
// on the page (like SEARCH_NAV_QUERY) have.
// Otherwise, the newly created entity won't have enough
// information to update the Apollo cache,
// and Apollo will silently give up and wipe the queries
// that want fields that the new entity lacks.
// Very confusing.
export const CREATE_ENTITY = gql`
  mutation CreateEntity($name: String!, $invited: String, $framework: String) {
    createEntity(
      input: {name: $name, defaultFramework: $framework, invited: $invited}
    ) {
      entity {
        id
        name
        photoUrl
        invitedTeam
        projects(first: 100) {
          edges {
            node {
              id
              name
              entityName
            }
          }
        }
      }
      apiKey {
        id
        name
      }
    }
  }
`;

export const CREATE_USER_FROM_ADMIN_MUTATION = gql`
  mutation CreateUserFromAdmin($email: String!, $admin: Boolean) {
    createUser(input: {email: $email, admin: $admin}) {
      user {
        id
        name
        username
        email
        admin
      }
    }
  }
`;

export interface CreateUserFromAdminMutationData {
  createUser: {
    user: {
      id: string;
      name: string;
      userInfo: UserInfo;
      entity: string;
    };
  };
}

export interface CreateUserFromAdminMutationVariables {
  email: string;
  admin: boolean;
}

export const USER_MUTATION = gql`
  mutation UpdateUser(
    $defaultEntity: String
    $defaultFramework: String
    $photoUrl: String
    $code: String
    $settingsVisited: Boolean
    $galleryVisited: Boolean
    $onboardingHidden: Boolean
  ) {
    updateUser(
      input: {
        defaultEntity: $defaultEntity
        defaultFramework: $defaultFramework
        photoUrl: $photoUrl
        code: $code
        settingsVisited: $settingsVisited
        galleryVisited: $galleryVisited
        onboardingHidden: $onboardingHidden
      }
    ) {
      user {
        id
        flags
        entity
      }
    }
  }
`;

export const UPDATE_USER_MUTATION = gql`
  mutation UpdateUserInfo(
    $name: String
    $photoUrl: String
    $userInfo: JSONString
    $defaultEntity: String
  ) {
    updateUser(
      input: {
        name: $name
        userInfo: $userInfo
        defaultEntity: $defaultEntity
        photoUrl: $photoUrl
      }
    ) {
      user {
        id
        name
        userInfo
        photoUrl
        entity
      }
    }
  }
`;

export interface UpdateUserMutationData {
  updateUser: {
    user: {
      id: string;
      name: string;
      userInfo: UserInfo;
      entity: string;
    };
  };
}

export interface UpdateUserMutationVariables {
  name?: string;
  photoUrl?: string;
  userInfo?: string;
  defaultEntity?: string;
}

export const REDEEM_HUB_ACCESS_CODE_MUTATION = gql`
  mutation RedeemHubAccessCode($code: String) {
    updateUser(input: {code: $code}) {
      user {
        id
        flags
      }
    }
  }
`;

export interface RedeemHubAccessCodeMutationData {
  updateUser: {
    user: {
      id: string;
      flags: string;
    };
  };
}

export interface RedeemHubAccessCodeMutationVariables {
  code: string;
}

export const GENERATE_API_KEY_MUTATION = gql`
  mutation GenerateApiKey($description: String) {
    generateApiKey(input: {description: $description}) {
      apiKey {
        id
        name
      }
    }
  }
`;

export interface GenerateApiKeyMutationData {
  generateApiKey: {
    apiKey: {
      id: string;
      name: string;
    };
  };
}

export interface GenerateApiKeyMutationVariables {
  description: string;
}

export const DELETE_API_KEY_MUTATION = gql`
  mutation DeleteApiKey($id: String!) {
    deleteApiKey(input: {id: $id}) {
      success
    }
  }
`;

export interface DeleteApiKeyMutationData {
  deleteApiKey: {
    success: boolean;
  };
}

export interface DeleteApiKeyMutationVariables {
  id: string;
}

export const UPDATE_ENTITY = gql`
  mutation UpdateEntity(
    $entityName: String!
    $defaultAccess: String
    $privateOnly: Boolean
    $codeSavingEnabled: Boolean
    $settings: SettingsInput
  ) {
    updateEntity(
      input: {
        entity: $entityName
        defaultAccess: $defaultAccess
        privateOnly: $privateOnly
        codeSavingEnabled: $codeSavingEnabled
        settings: $settings
      }
    ) {
      entity {
        id
        defaultAccess
        privateOnly
        limits
        settings {
          defaultCloudRegion {
            id
            provider
            region
          }
        }
      }
    }
  }
`;

export interface UpdateEntityMutationData {
  updateEntity: {
    entity: {
      id: string;
      defaultAccess: string;
      privateOnly: boolean;
      limits: string;
      settings: Settings;
    };
  };
}

export interface UpdateEntityMutationVariables {
  entityName: string;
  defaultAccess?: string;
  privateOnly?: boolean;
  codeSavingEnabled?: boolean;
  settings?: SettingsInput;
}

export const CLAIM_ANONYMOUS_ENTITY = gql`
  mutation ClaimAnonymousEntity($anonymousApiKey: String!) {
    claimAnonymousEntity(input: {anonymousApiKey: $anonymousApiKey}) {
      task {
        id
        type
        state
        progress
        name
      }
    }
  }
`;

export const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($email: String!) {
    resetPassword(input: {email: $email}) {
      success
    }
  }
`;

export interface ResetPasswordData {
  success: boolean;
}

export interface ResetPasswordVars {
  email: string;
}
